import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { supabaseLeaseholderService } from '../../../../services/supabase/leaseholders';
import type { Leaseholder } from '../../../../types';
import { useClickOutside } from '../../../../hooks/useClickOutside';

interface AddEditLeaseholderModalProps {
  leaseholder: Leaseholder | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddEditLeaseholderModal({ 
  leaseholder,
  onClose, 
  onSuccess
}: AddEditLeaseholderModalProps) {
  const modalContentRef = useRef<HTMLDivElement>(null);
  useClickOutside(modalContentRef, onClose);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    holder_name: leaseholder?.holder_name || '',
    holder_contact: leaseholder?.holder_contact || '',
    holder_phone: leaseholder?.holder_phone || '',
    holder_email: leaseholder?.holder_email || '',
    holder_position: leaseholder?.holder_position || '',
    holder_phy_add01: leaseholder?.holder_phy_add01 || '',
    holder_phy_add02: leaseholder?.holder_phy_add02 || '',
    holder_phy_sub: leaseholder?.holder_phy_sub || '',
    holder_phy_state: leaseholder?.holder_phy_state || '',
    holder_phy_pcode: leaseholder?.holder_phy_pcode || '',
    holder_post_add01: leaseholder?.holder_post_add01 || '',
    holder_post_add02: leaseholder?.holder_post_add02 || '',
    holder_post_sub: leaseholder?.holder_post_sub || '',
    holder_post_state: leaseholder?.holder_post_state || '',
    holder_pst_pcode: leaseholder?.holder_pst_pcode || '',
    holder_abn: leaseholder?.holder_abn?.toString() || '',
    holder_acn: leaseholder?.holder_acn?.toString() || '',
    holder_indcn: leaseholder?.holder_indcn?.toString() || ''
  });

  useEffect(() => {
    if (leaseholder) {
      setFormData({
        holder_name: leaseholder.holder_name || '',
        holder_contact: leaseholder.holder_contact || '',
        holder_phone: leaseholder.holder_phone || '',
        holder_email: leaseholder.holder_email || '',
        holder_position: leaseholder.holder_position || '',
        holder_phy_add01: leaseholder.holder_phy_add01 || '',
        holder_phy_add02: leaseholder.holder_phy_add02 || '',
        holder_phy_sub: leaseholder.holder_phy_sub || '',
        holder_phy_state: leaseholder.holder_phy_state || '',
        holder_phy_pcode: leaseholder.holder_phy_pcode || '',
        holder_post_add01: leaseholder.holder_post_add01 || '',
        holder_post_add02: leaseholder.holder_post_add02 || '',
        holder_post_sub: leaseholder.holder_post_sub || '',
        holder_post_state: leaseholder.holder_post_state || '',
        holder_pst_pcode: leaseholder.holder_pst_pcode || '',
        holder_abn: leaseholder.holder_abn?.toString() || '',
        holder_acn: leaseholder.holder_acn?.toString() || '',
        holder_indcn: leaseholder.holder_indcn?.toString() || ''
      });
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [leaseholder]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    // Only validate Business Name as required
    if (!formData.holder_name.trim()) {
      setError('Business Name is required');
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepare data - ensure all fields are properly formatted
      const data = {
        ...formData,
        // Only keep the ABN and INDCN fields if they have values
        holder_abn: formData.holder_abn || null,
        holder_acn: formData.holder_acn ? parseInt(formData.holder_acn) : null,
        holder_indcn: formData.holder_indcn ? parseInt(formData.holder_indcn) : null,
        // Ensure other fields are properly formatted
        holder_contact: formData.holder_contact.trim() || null,
        holder_phone: formData.holder_phone.trim() || null,
        holder_email: formData.holder_email.trim() || null,
        holder_position: formData.holder_position.trim() || null,
        holder_phy_add01: formData.holder_phy_add01.trim() || null,
        holder_phy_add02: formData.holder_phy_add02.trim() || null,
        holder_phy_sub: formData.holder_phy_sub.trim() || null,
        holder_phy_state: formData.holder_phy_state || null,
        holder_phy_pcode: formData.holder_phy_pcode.trim() || null,
        holder_post_add01: formData.holder_post_add01.trim() || null,
        holder_post_add02: formData.holder_post_add02.trim() || null,
        holder_post_sub: formData.holder_post_sub.trim() || null,
        holder_post_state: formData.holder_post_state || null,
        holder_pst_pcode: formData.holder_pst_pcode.trim() || null
      };

      console.log('Submitting leaseholder data:', data);

      if (leaseholder) {
        await supabaseLeaseholderService.update(leaseholder.holder_id, data);
      } else {
        await supabaseLeaseholderService.create(data);
      }
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error saving leaseholder:', err);
      
      // Improved error handling
      let errorMessage = 'Failed to save leaseholder';
      
      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'object' && err !== null) {
        // Try to extract more detailed error information
        try {
          const errorObj = JSON.stringify(err, null, 2);
          errorMessage = `Error details: ${errorObj}`;
          
          // Check for specific constraint violations
          if (errorObj.includes('leaseholders_holder_phy_pcode_check')) {
            errorMessage = 'Physical address postcode format is invalid. Please leave it blank or enter a valid postcode.';
          } else if (errorObj.includes('violates check constraint')) {
            errorMessage = 'One or more fields have invalid formats. Please check your input or leave fields blank.';
          }
        } catch (jsonErr) {
          console.error('Error stringifying error object:', jsonErr);
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center p-0 z-[100] overflow-y-auto"
    >
      <div 
        ref={modalContentRef}
        className="relative bg-white w-full md:max-w-2xl md:rounded-lg md:my-8 min-h-screen md:min-h-0 max-h-full md:max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 md:p-6 border-b bg-white">
          <h3 className="text-xl font-semibold text-gray-900">
            {leaseholder ? 'Edit Leaseholder' : 'Add New Leaseholder'}
          </h3>
          <button 
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content - with scrollable area */}
        <form id="leaseholder-form" onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Basic Information */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Business Name *
                </label>
                <input
                  type="text"
                  value={formData.holder_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, holder_name: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Position
                </label>
                <input
                  type="text"
                  value={formData.holder_position}
                  onChange={(e) => setFormData(prev => ({ ...prev, holder_position: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Contact Person
                </label>
                <input
                  type="text"
                  value={formData.holder_contact}
                  onChange={(e) => setFormData(prev => ({ ...prev, holder_contact: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.holder_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, holder_email: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.holder_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, holder_phone: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                />
              </div>
            </div>
          </div>

          {/* Identification */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Identification</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  ABN
                </label>
                <input
                  type="text"
                  value={formData.holder_abn}
                  onChange={(e) => setFormData(prev => ({ ...prev, holder_abn: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  pattern="[0-9]{11}"
                  title="ABN must be 11 digits"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  ACN
                </label>
                <input
                  type="text"
                  value={formData.holder_acn}
                  onChange={(e) => setFormData(prev => ({ ...prev, holder_acn: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  ICN
                </label>
                <input
                  type="text"
                  value={formData.holder_indcn}
                  onChange={(e) => setFormData(prev => ({ ...prev, holder_indcn: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                />
              </div>
            </div>
          </div>

          {/* Physical Address */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Physical Address</h4>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Address Line 1
                </label>
                <input
                  type="text"
                  value={formData.holder_phy_add01}
                  onChange={(e) => setFormData(prev => ({ ...prev, holder_phy_add01: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Address Line 2
                </label>
                <input
                  type="text"
                  value={formData.holder_phy_add02}
                  onChange={(e) => setFormData(prev => ({ ...prev, holder_phy_add02: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    City/Suburb
                  </label>
                  <input
                    type="text"
                    value={formData.holder_phy_sub}
                    onChange={(e) => setFormData(prev => ({ ...prev, holder_phy_sub: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    State
                  </label>
                  <select
                    value={formData.holder_phy_state}
                    onChange={(e) => setFormData(prev => ({ ...prev, holder_phy_state: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  >
                    <option value="">Select state</option>
                    <option value="NT">Northern Territory</option>
                    <option value="NSW">New South Wales</option>
                    <option value="VIC">Victoria</option>
                    <option value="QLD">Queensland</option>
                    <option value="WA">Western Australia</option>
                    <option value="SA">South Australia</option>
                    <option value="TAS">Tasmania</option>
                    <option value="ACT">Australian Capital Territory</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Postcode
                  </label>
                  <input
                    type="text"
                    value={formData.holder_phy_pcode}
                    onChange={(e) => setFormData(prev => ({ ...prev, holder_phy_pcode: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                    pattern="[0-9]{4}"
                    title="Postcode must be 4 digits"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Postal Address */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Postal Address</h4>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Address Line 1
                </label>
                <input
                  type="text"
                  value={formData.holder_post_add01}
                  onChange={(e) => setFormData(prev => ({ ...prev, holder_post_add01: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Address Line 2
                </label>
                <input
                  type="text"
                  value={formData.holder_post_add02}
                  onChange={(e) => setFormData(prev => ({ ...prev, holder_post_add02: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    City/Suburb
                  </label>
                  <input
                    type="text"
                    value={formData.holder_post_sub}
                    onChange={(e) => setFormData(prev => ({ ...prev, holder_post_sub: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    State
                  </label>
                  <select
                    value={formData.holder_post_state}
                    onChange={(e) => setFormData(prev => ({ ...prev, holder_post_state: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                  >
                    <option value="">Select state</option>
                    <option value="NT">Northern Territory</option>
                    <option value="NSW">New South Wales</option>
                    <option value="VIC">Victoria</option>
                    <option value="QLD">Queensland</option>
                    <option value="WA">Western Australia</option>
                    <option value="SA">South Australia</option>
                    <option value="TAS">Tasmania</option>
                    <option value="ACT">Australian Capital Territory</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Postcode
                  </label>
                  <input
                    type="text"
                    value={formData.holder_pst_pcode}
                    onChange={(e) => setFormData(prev => ({ ...prev, holder_pst_pcode: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
                    pattern="[0-9]{4}"
                    title="Postcode must be 4 digits"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Audit Information - Only shown when editing */}
          {leaseholder && leaseholder.created_date && (
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Audit Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Created Date</label>
                  <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                    {new Date(leaseholder.created_date).toLocaleDateString()}
                  </div>
                </div>
                {leaseholder.modified_date && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Modified Date</label>
                    <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                      {new Date(leaseholder.modified_date).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex justify-end gap-3 p-4 md:p-6 border-t bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="leaseholder-form"
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-md disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : (leaseholder ? 'Save Changes' : 'Create Leaseholder')}
          </button>
        </div>
      </div>
    </div>
  );
}
